import { get, post } from '@alltrails/shared/api';
import { UserId } from '@alltrails/shared/types/User';
import Brand from 'types/Modifiers/Brand';

// copied from './schema', but not available in mugen when we migrate
// type GroupEditResponse = components['schemas']['CreateEditGroup'];
// type EditGroup = components['schemas']['CreateEditGroupItem'];

export type EditGroupId = Brand<number, 'EditGroupId'>;
export type EditGroupItemId = Brand<number, 'EditGroupItemId'>;

export type EditGroup = {
  id?: EditGroupId;
  /**
   * @description Type of object, e.g. trail or area
   */
  type?: string;
  user?: Record<string, never>;
  system_generated?: boolean;
  /**
   * @description ID of type object, e.g. trail ID or area ID
   */
  targetObjectID?: number;
  editItems?: Record<string, never>[];
  metadata?: {
    status?: string;
    /**
     * @description iso8601 formatted date
     */
    created?: string;
    /**
     * @description iso8601 formatted date
     */
    updated?: string;
  };
};

export type CreateEditGroupRequestPayload = {
  type: 'trail' | 'area';
  /**
   * @description ID of the type (e.g. trail id or area id)
   */
  target_object_id?: number;
  system_generated?: boolean;
  /**
   * @description AtMap ID
   */
  origin_object_id?: number;
};

export type CreateEditGroupResponse = {
  editGroups?: EditGroup[];
  meta?: {
    status?: string;
    items?: number;
    /**
     * @description iso8601 formatted date
     */
    timestamp?: string;
  };
};

export async function createEditGroup(payload: CreateEditGroupRequestPayload) {
  return post<CreateEditGroupResponse>('/api/alltrails/edits/groups', payload);
}

export type CreateEditGroupItemRequestPayload = {
  type: 'report' | 'trail_detail';
  data?: {
    name?: string;
    value?: string;
  };
  /**
   * @description Defaults to trail
   * @enum {string}
   */
  group_type?: 'trail' | 'area';
  /**
   * @description ID of group type object (e.g. trail ID or area ID)
   */
  relation_id?: number;
};

export type CreateEditGroupItemResponse = {
  editItems?: {
    id?: EditGroupItemId;
    editGroupId?: EditGroupId;
    /** @enum {string} */
    type?: 'report';
    data?: {
      comment?: string;
    };
    /**
     * @description ID of item the comment is about, e.g. trail ID or area ID
     */
    relationId?: number;
    targetObjectType?: string;
    targetObjectId?: number;
    userId?: UserId;
    voteCounts?: {
      yes?: number;
      no?: number;
    };
    relationObject?: Record<string, never>;
    metadata?: {
      status?: string;
      statusReason?: string;
      created?: string;
      updated?: string;
    };
  }[];
  meta?: {
    status?: string;
    items?: number;
    timestamp?: string;
  };
};

export async function createEditGroupItem(editGroupId: EditGroupId, payload: CreateEditGroupItemRequestPayload) {
  return post<CreateEditGroupItemResponse>(`/api/alltrails/edits/groups/${editGroupId}/items`, payload);
}

export type ApplyEditsRequestParams = {
  type: 'trail';
  target_object_id: number;
};

export type ApplyEditsResponse = {
  area?: {
    id?: number;
    name?: string;
  };
  attributes?: Record<string, unknown>;
  avgRating?: number;
  defaultActivityStatus?: Record<string, unknown>;
  defaultMap?: unknown;
  defaultPhoto?: Record<string, unknown>;
  id: number;
  location?: Record<string, unknown>;
  metadata?: {
    created?: string;
    status?: string;
    updated?: string;
  };
  name?: string;
  overview?: string;
  overview_source?: string;
  popularity?: number;
  routeType?: {
    name?: string;
    uid?: string;
  };
  slug?: string;
  source?: number;
  trailCounts?: Record<string, unknown>;
  trailGeoStats?: Record<string, unknown>;
  versionId?: number;
};

export async function applyEdits(params: ApplyEditsRequestParams) {
  return get<ApplyEditsResponse>('/api/alltrails/edits/apply', { params });
}
